
/**
 * Generates kid-friendly baking and cooking tips based on instruction keywords.
 */

export const generateBakingTip = (instruction: string): string | null => {
  const tips: Record<string, string> = {
    // General Kitchen Tips
    'mix': "Remember to mix everything slowly at first so it doesn't splash! Once it's combined, you can mix faster! 🌪️",
    'stir': "Stir in a figure-eight pattern - it's like drawing infinity symbols! ∞",
    'pour': "Pour slowly and carefully, just like when you're helping water the plants! 🌱",
    'cut': "Always ask a grown-up to help with cutting - safety first, young chef! ✂️",
    'chop': "Remember to keep your fingers tucked away while chopping - like a little turtle in its shell! 🐢",
    
    // Baking Specific - will be filtered by context aware filtering
    'knead': "Kneading dough is like playing with playdough - push, fold, and repeat! 🎨",
    'cream': "When mixing ingredients well, count to 30 while you stir - that's the magic number! 🎵",
    'whisk': "When you whisk, hold it like a pencil and make big circles - it's like drawing in your bowl! ✏️",
    'fold': "Folding ingredients together means being extra gentle - like you're tucking them into bed! 🤗",
    'bake': "While it bakes, the heat is doing all the work - it's like magic happening in your oven! 🎁",
    
    // Cooking Specific
    'sauté': "When you sauté, the food should dance in the pan - like tiny dancers at a party! 💃",
    'simmer': "A simmer is like tiny bubbles having a gentle party in your pot! 🫧",
    'boil': "When water boils, it looks like a bubble volcano erupting! 🌋",
    'fry': "Watch the food turn golden brown - like the sun setting in your pan! 🌅",
    'season': "Adding seasoning is like sprinkling magic dust on your food! ✨",
    'heat': "Heating your pan first helps food cook better - like warming up before exercising! 🔥",
    
    // Measurement Tips
    'measure': "Level off your measuring cups with the back of a knife - like smoothing sand in a sandbox! ⏳",
    'spoon': "Use two spoons like a see-saw to transfer ingredients! 🎪",
    'sprinkle': "Sprinkle ingredients from high up - like you're making it snow in your kitchen! ❄️",
    'pat': "Pat your food gently, like giving a puppy little pats! 🐕",
    
    // Dip-specific tips
    'dip': "Dips are perfect for parties - everyone can gather around and share! 🎉",
    'cheese': "When cheese melts, it gets all stretchy and gooey - it's kitchen magic! 🧀",
    'stir_dip': "Stir everything until it's well combined - like mixing paints to make a new color! 🎨",
    'hot sauce': "Hot sauce adds a spicy kick - like adding excitement to your recipe! 🌶️",
    'ranch': "Ranch gives a cool, creamy taste - it's like a flavor superhero! 🦸‍♂️",
    'chicken': "Chicken is full of protein to help you grow strong muscles! 💪",
    'layer': "Layering ingredients is like building a tasty tower! 🏗️",
    'shred': "Shredding cheese is like giving it a tiny haircut! 💇‍♂️",
    'top': "Toppings are the finishing touch - like putting a crown on your recipe! 👑",
    'serve': "Serving food is like presenting a gift you made with love! 🎁",
    'warm': "Keep your dip warm so it stays gooey and delicious! 🔥",
    'scoop': "Each scoop is a little treasure chest of flavors! 💎"
  };

  // Convert the instruction to lowercase for case-insensitive matching
  const instructionLower = instruction.toLowerCase();
  
  // Special case for "stir" in dips: check if it's a dip context first
  if (instructionLower.includes('stir') && (
      instructionLower.includes('dip') || 
      instructionLower.includes('mix') || 
      instructionLower.includes('combine')
    )) {
    return tips['stir_dip'];
  }
  
  // Find the first matching tip
  for (const [keyword, tip] of Object.entries(tips)) {
    if (instructionLower.includes(keyword)) {
      return tip;
    }
  }

  return null;
};
